/**
 * T+N 结算倒计时(收益面板待打款项)。口径见 settlement-rules §二(默认 T+7)。
 * 纯函数 + 一个每秒 tick 的 hook。前端只展示倒计时,不参与结算计算。
 */
import { useEffect, useState } from 'react';

export interface SettleCountdown {
  /** 解析失败(`settleDeadlineAt` 非法)。 */
  invalid: boolean;
  /** 已过 deadline。 */
  overdue: boolean;
  /** 剩余 < 24h(高亮「今天打款」)。 */
  dueToday: boolean;
  /** 人类可读文案。 */
  label: string;
}

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** 计算某一时刻相对 now 的倒计时态。 */
export function computeSettleCountdown(
  settleDeadlineAt: string | null | undefined,
  now: number,
): SettleCountdown {
  if (!settleDeadlineAt) {
    return { invalid: true, overdue: false, dueToday: false, label: '待结算' };
  }
  const deadline = Date.parse(settleDeadlineAt);
  if (Number.isNaN(deadline)) {
    return { invalid: true, overdue: false, dueToday: false, label: '待结算' };
  }
  const remain = deadline - now;
  if (remain <= 0) {
    return { invalid: false, overdue: true, dueToday: false, label: '已逾期' };
  }
  if (remain < DAY_MS) {
    const hours = Math.max(1, Math.ceil(remain / HOUR_MS));
    return { invalid: false, overdue: false, dueToday: true, label: `今天打款 · ${hours}h 内` };
  }
  const days = Math.ceil(remain / DAY_MS);
  return { invalid: false, overdue: false, dueToday: false, label: `预计 ${days} 天后打款` };
}

/** 每秒 tick 的倒计时 hook;逾期/非法后停 tick(无意义刷新)。 */
export function useSettleCountdown(settleDeadlineAt: string | null | undefined): SettleCountdown {
  const [now, setNow] = useState(() => Date.now());
  const state = computeSettleCountdown(settleDeadlineAt, now);
  const stopped = state.invalid || state.overdue;

  useEffect(() => {
    if (stopped) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [stopped, settleDeadlineAt]);

  return state;
}
